<template>
  <div v-if="!isLoadingRule" class="c-partner-mapping-single-filter">
    <BittsDropdownMenu
      v-if="!showFilter"
      v-model:is-open="isDropdownOpen"
      :options="dropdownOptions"
      :use-search="true"
      search-placeholder="Search field"
      variant="default"
      mode="single"
      :disabled="disabled || isLoadingOptions"
      @selected:options="handleFieldSelected"
    >
      <BittsButton
        text="Add Filter"
        variant="outline"
        size="small"
        :left-icon="['far', 'plus']"
        class="c-partner-mapping-single-filter__button"
        :disabled="disabled"
      />
    </BittsDropdownMenu>
    <div v-else class="c-partner-mapping-single-filter__filter-container">
      <ComplexFilterNestedGroup
        variant="root"
        :hide-add-filter="true"
        :hide-combinator="true"
        :hide-delete="true"
        :filter-ids="filterIds"
        :filters="filters"
        :field-options="filteredFieldOptions"
        :disabled="disabled"
        @update:filters="handleFiltersUpdate"
        @update:filter-ids="handleFilterIdsUpdate"
      />
      <div v-if="!hideActions" class="c-partner-mapping-single-filter__actions">
        <BittsButton
          text="Clear"
          size="small"
          variant="outline"
          :disabled="disabled || isSaving"
          @click="handleClearFilters"
        />
        <BittsButton
          text="Apply"
          size="small"
          :disabled="disabled || isSaving || !hasValidChanges"
          @click="handleApplyFilters"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsButton, BittsDropdownMenu } from '@crossbeam/bitts';

import { computed, onMounted, ref, watch } from 'vue';

import ComplexFilterNestedGroup, {
  type NestedGroupFilter,
} from '@/components/filters/ComplexFilterNestedGroup.vue';
import { isFilterComplete } from '@/components/filters/types';

import { crossbeamApi } from '@/api';
import useIteratively from '@/composables/useIteratively';
import { getDefaultsForDataType } from '@/constants/filter-operators';
import { useFlashesStore, useSourcesStore } from '@/stores';
import {
  PartnerMappingSourceFilterExpressionsType,
  PartnersInternalPopulationPayload,
} from '@/types/partners_internal_populations';

import {
  type FieldOptionWithSource,
  usePartnerMappingFieldOptions,
} from '../composables/usePartnerMappingFieldOptions';
import { findPartnerMappingRule } from '../partnerMappingRules';

type Props = {
  feedId: number;
  disabled?: boolean;
  hideActions?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  hideActions: false,
});

const flashesStore = useFlashesStore();
const sourcesStore = useSourcesStore();
const { iteratively } = useIteratively();

const {
  fieldOptions,
  groupedFieldOptions,
  isLoadingOptions,
  loadFieldOptions,
} = usePartnerMappingFieldOptions(() => props.feedId);

const isDropdownOpen = ref(false);
const isLoadingRule = ref(false);
const isSaving = ref(false);
const existingRuleId = ref<number | null>(null);
const selectedSourceId = ref<number | null>(null);
const filters = ref<Map<string, NestedGroupFilter>>(new Map());
const filterIds = ref<string[]>([]);
const initialFilterState = ref<{
  filters: Map<string, NestedGroupFilter>;
  filterIds: string[];
  selectedSourceId: number | null;
} | null>(null);

const showFilter = computed(() => filterIds.value.length > 0);

const currentFilterId = computed(() => filterIds.value[0] ?? null);

const partnerMappingFilter = computed<NestedGroupFilter | null>(() => {
  const id = currentFilterId.value;
  if (!id) return null;
  return filters.value.get(id) ?? null;
});

const dropdownOptions = computed(() => {
  if (groupedFieldOptions.value.length === 0) return [];

  return groupedFieldOptions.value
    .map((group) => ({
      label: group.label,
      useBorder: true,
      options: group.options
        .filter((field): field is FieldOptionWithSource => field.value != null)
        .map((field) => ({
          label: field.label,
          value: field.value,
          sourceId: field.sourceId,
        })),
    }))
    .filter((group) => group.options.length > 0);
});

const filteredFieldOptions = computed(() => {
  if (selectedSourceId.value == null) return fieldOptions.value;

  const currentFilterValue = partnerMappingFilter.value?.value;
  return fieldOptions.value.filter((field) => {
    if (field.value === currentFilterValue) return true;
    return field.sourceId === selectedSourceId.value;
  });
});

const hasValidChanges = computed(() => {
  const current = partnerMappingFilter.value;
  if (!showFilter.value || !current || !isFilterComplete(current)) {
    return false;
  }

  if (!initialFilterState.value) return true;

  const initialFilterId = initialFilterState.value.filterIds[0];
  if (!initialFilterId) return true;

  const initialFilter = initialFilterState.value.filters.get(initialFilterId);
  if (!initialFilter) return true;

  const hasValueChange =
    current.value !== initialFilter.value ||
    current.operator !== initialFilter.operator ||
    JSON.stringify(current.operand) !== JSON.stringify(initialFilter.operand);
  const hasSourceChange =
    selectedSourceId.value !== initialFilterState.value.selectedSourceId;

  return hasValueChange || hasSourceChange;
});

const accountSource = computed(() =>
  sourcesStore.getSourceByFeedIdAndMdmType(props.feedId, 'account'),
);

const sourceFilterExpressions = computed(() => {
  const current = partnerMappingFilter.value;
  return current && isFilterComplete(current)
    ? convertFilterToSourceExpressions()
    : null;
});

function handleFilterIdsUpdate(ids: string[]) {
  const limitedIds = ids.slice(0, 1);
  filterIds.value = limitedIds;

  const newFilters = new Map(filters.value);
  for (const id of newFilters.keys()) {
    if (!limitedIds.includes(id)) newFilters.delete(id);
  }
  filters.value = newFilters;

  if (limitedIds.length === 0) selectedSourceId.value = null;
}

function handleFiltersUpdate(newFilters: Map<string, NestedGroupFilter>) {
  filters.value = newFilters;

  const filterId = currentFilterId.value;
  if (filterId) {
    const filter = newFilters.get(filterId);
    if (filter?.value) {
      const field = fieldOptions.value.find((f) => f.value === filter.value);
      if (field?.sourceId) selectedSourceId.value = field.sourceId;
    }
  }
}

function handleFieldSelected(options: unknown[]) {
  const selectedOption = options[0] as
    | { value: number; sourceId?: number }
    | undefined;
  if (!selectedOption?.value) return;

  const field = fieldOptions.value.find(
    (f) => f.value === selectedOption.value,
  );
  if (!field) return;

  // Store the sourceId of the selected field
  selectedSourceId.value = field.sourceId ?? selectedOption.sourceId ?? null;

  const defaults = getDefaultsForDataType(field.dataType);
  const filterId = crypto.randomUUID();

  filters.value = new Map([
    [
      filterId,
      {
        label: filterId,
        value: field.value,
        operator: field.allowedOperators?.at(0) ?? defaults.operator,
        operand: defaults.operand,
      },
    ],
  ]);
  filterIds.value = [filterId];
  initialFilterState.value = null;
}

function convertFilterToSourceExpressions(): PartnerMappingSourceFilterExpressionsType | null {
  const filter = partnerMappingFilter.value;
  if (!filter?.value || !filter.operator || !selectedSourceId.value) {
    return null;
  }

  const filterId = currentFilterId.value;
  if (!filterId) return null;

  // Get the source for the selected field
  const source = sourcesStore
    .getSourcesByFeedId(props.feedId)
    .find((s) => s.id === selectedSourceId.value);
  if (!source?.table) return null;

  const label = filter.label || filterId;

  const filterParts = [
    {
      label,
      source_field_id: filter.value as number,
      operator: filter.operator,
      operand: filter.operand ?? null,
    },
  ];

  return {
    [source.table]: {
      filter_expression: [label],
      filter_parts: filterParts,
    },
  } as PartnerMappingSourceFilterExpressionsType;
}

async function loadExistingRule() {
  if (!props.feedId || !accountSource.value?.id) return;

  isLoadingRule.value = true;
  try {
    const { data, error } = await crossbeamApi.GET(
      '/v0.1/partners-internal-populations',
    );

    if (error) {
      console.error('Failed to load partner mapping rules:', error);
      return;
    }

    // Find the rule for this feed's Account source
    const populations = data?.items || [];
    const rule = findPartnerMappingRule(
      populations,
      props.feedId,
      accountSource.value?.id,
    );

    if (!rule?.source_filter_expressions || !rule.id) {
      return;
    }

    existingRuleId.value = rule.id;

    // Convert API format to filter format
    const sourceFilterExpressions = rule.source_filter_expressions;
    const tableNames = Object.keys(sourceFilterExpressions);
    if (tableNames.length === 0) return;

    // For now, we only support one filter, so take the first table
    const tableName = tableNames[0];
    if (!tableName) return;
    const filterData = sourceFilterExpressions[tableName];
    if (!filterData?.filter_parts?.length) return;

    // Find the source for this table
    const source = sourcesStore
      .getSourcesByFeedId(props.feedId)
      .find((s) => s.table === tableName);
    if (!source?.id) return;

    selectedSourceId.value = source.id;

    // Convert filter parts to NestedGroupFilter format
    const filterPart = filterData.filter_parts[0];
    if (!filterPart) return;
    const filterId = crypto.randomUUID();

    const initialFilter: NestedGroupFilter = {
      label: filterPart.label || filterId,
      value: filterPart.source_field_id,
      operator: filterPart.operator as NestedGroupFilter['operator'],
      operand: filterPart.operand,
    };

    filters.value = new Map([[filterId, initialFilter]]);
    filterIds.value = [filterId];

    initialFilterState.value = {
      filters: new Map([[filterId, { ...initialFilter }]]),
      filterIds: [filterId],
      selectedSourceId: source.id,
    };
  } finally {
    isLoadingRule.value = false;
  }
}

function stringifyOperand(operand: unknown): string {
  if (operand == null) return '';
  if (Array.isArray(operand)) return operand.map(String).join(', ');
  if (typeof operand === 'object') return JSON.stringify(operand);
  return String(operand);
}

async function handleClearFilters() {
  isSaving.value = true;
  try {
    let ruleIdToDelete: number | null = existingRuleId.value;

    if (ruleIdToDelete == null && props.feedId && accountSource.value?.id) {
      const { data, error } = await crossbeamApi.GET(
        '/v0.1/partners-internal-populations',
      );
      if (!error && data?.items) {
        const rule = findPartnerMappingRule(
          data.items,
          props.feedId,
          accountSource.value?.id,
        );
        if (rule?.id) ruleIdToDelete = rule.id;
      }
    }

    if (ruleIdToDelete == null) {
      resetFilterState();
      return;
    }

    const { error } = await crossbeamApi.DELETE(
      '/v0.1/partners-internal-population/{id}',
      {
        params: { path: { id: ruleIdToDelete } },
      },
    );
    if (error) {
      flashesStore.addErrorFlash({
        message: 'Failed to remove Partner rule.',
      });
      return;
    }

    const sourceId = accountSource.value?.id;
    if (sourceId != null) {
      iteratively.userRemovedPartnerMappingRule({
        feed_id: props.feedId,
        source_id: sourceId,
      });
    }

    resetFilterState();

    flashesStore.addSuccessFlash({
      message: 'Your Partner rule has been removed',
    });
  } catch (err) {
    flashesStore.addErrorFlash({
      message: 'Failed to remove Partner rule.',
    });
  } finally {
    isSaving.value = false;
  }
}

function failSave(): false {
  flashesStore.addErrorFlash({ message: 'Failed to save Partner rule.' });
  return false;
}

async function handleApplyFilters(): Promise<boolean> {
  if (!accountSource.value?.id) {
    console.error('Account source not found for feed');
    return failSave();
  }

  const sourceId = accountSource.value.id;
  const isNew = existingRuleId.value == null;

  isSaving.value = true;
  try {
    const sourceFilterExpressions = convertFilterToSourceExpressions();

    if (
      !sourceFilterExpressions ||
      Object.keys(sourceFilterExpressions).length === 0
    ) {
      return failSave();
    }

    // Create or update the rule
    const payload = {
      source_id: accountSource.value.id,
      source_filter_expressions: sourceFilterExpressions,
    } as PartnersInternalPopulationPayload;

    const { data, error } = await crossbeamApi.PUT(
      '/v0.1/partners-internal-population',
      { body: payload },
    );
    if (error) {
      return failSave();
    }
    if (data?.id) {
      existingRuleId.value = data.id;
    }

    const filterId = currentFilterId.value;
    const currentFilter = filterId ? filters.value.get(filterId) : undefined;
    if (filterId && currentFilter) {
      initialFilterState.value = {
        filters: new Map([[filterId, { ...currentFilter }]]),
        filterIds: [filterId],
        selectedSourceId: selectedSourceId.value,
      };
    }

    if (currentFilter) {
      const ruleField = fieldOptions.value.find(
        (field) => field.value === currentFilter.value,
      );
      iteratively.userSavedPartnerMappingRule({
        feed_id: props.feedId,
        source_id: sourceId,
        rule_field: ruleField?.label ?? String(currentFilter.value ?? ''),
        rule_operator: currentFilter.operator ?? '',
        rule_value: stringifyOperand(currentFilter.operand),
        is_new: isNew,
      });
    }

    flashesStore.addSuccessFlash({
      message: 'Your Partner rule has been saved',
    });
    return true;
  } catch (err) {
    return failSave();
  } finally {
    isSaving.value = false;
  }
}

// Initialize on mount
onMounted(async () => {
  await sourcesStore.readySync;
  if (props.feedId) {
    loadFieldOptions();
    await loadExistingRule();
  }
});

function resetFilterState() {
  existingRuleId.value = null;
  initialFilterState.value = null;
  filters.value = new Map();
  filterIds.value = [];
  selectedSourceId.value = null;
}

// Reload options and rule when feedId changes; reset state so stale data from previous feed does not persist
watch(
  () => props.feedId,
  async (newFeedId) => {
    resetFilterState();
    if (newFeedId) {
      await sourcesStore.readySync;
      loadFieldOptions();
      await loadExistingRule();
    } else {
      loadFieldOptions();
    }
  },
  { immediate: false },
);

watch(isDropdownOpen, async (isOpen) => {
  if (isOpen && props.feedId) {
    await sourcesStore.readySync;
    loadFieldOptions();
  }
});

defineExpose({
  save: handleApplyFilters,
  hasValidChanges,
  sourceFilterExpressions,
});
</script>

<style lang="pcss" scoped>
@reference "../../../../../css/theme.css";
.c-partner-mapping-single-filter {
  @apply flex flex-col;
}

.c-partner-mapping-single-filter__button {
  @apply bg-neutral-background-weak w-fit self-start;
}

.c-partner-mapping-single-filter__filter-container {
  @apply flex gap-6 justify-between items-center;
}

.c-partner-mapping-single-filter__actions {
  @apply flex items-center gap-6;
}
</style>
